import { initializePaddle, type Paddle, type PaddleEventData } from '@paddle/paddle-js';
import { useUrlSearchParams } from '@vueuse/core';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';

import { useSubscriptionStore } from '@/common/stores/subscription';
import { track } from '@/helpers/analytics/mixpanel';
import { axios } from '@/helpers/axios';
import type { PaddlePricePreview,Period, Plan } from '@/types/global';


export const usePaddleStore = defineStore('paddle', () => {
  track('Paddle.js loaded');
  const paddle = ref<Paddle | null>(null);
  const loading = ref(false);
  const loadingPricePreview = ref<boolean>(false);
  const pricePreviewData = ref<PaddlePricePreview | null>(null);
  const route = useRoute();
  const isNewYear = computed(() => route.path === '/a/newyear');

  const loadPaddle = async () => {
    loading.value = true;

    try {
      const paddleObj = await initializePaddle({
        environment: import.meta.env.VITE_PADDLE_ENVIROMENT,
        token: import.meta.env.VITE_PADDLE_CLIENT_TOKEN,
        eventCallback: (event: PaddleEventData) => {
          const subscriptionStore = useSubscriptionStore();

          const eventName = event.name;
          const selectedPlan = subscriptionStore.selectedPlan;
          const params = useUrlSearchParams<{ _ptxn: string }>('history');
          const transactionData = event.data;

          switch(eventName) {
            case 'checkout.customer.created':
              subscriptionStore.saveCustomer(event);
              break;
            case 'checkout.customer.updated':
              subscriptionStore.saveCustomer(event);
              break;
            case 'checkout.completed':
              //  if it is an update payment methods transaction
              if (params._ptxn === transactionData.transaction_id) {
                window.location.href = '/settings?success=Your payment method has been updated';
              } else {
                subscriptionStore.currentPlan = selectedPlan;
              }
              break;
          }
        },
      });

      paddle.value = paddleObj;
    } catch (error) {
      console.error('Failed to load Paddle.js:', error);
      track('Paddle.js failed to load', { error: error.message });
      // Silently fail - Paddle is not critical for page load
    } finally {
      loading.value = false;
    }
  };

  const getFPTid = () => window.FPROM && window.FPROM.data.tid;

  const openCheckout = (planType: Plan, email: string, period: Period) => {
    if (!paddle.value) {
      return;
    }

    const normalizedPeriod: Period = period || 'monthly';

    const discountCode =
      isNewYear.value && normalizedPeriod === 'monthly'
        ? 'NEWYEAR'
        : null;

    const paddleProductId = window.__PLANS__.product_prices[normalizedPeriod][planType];
    paddle.value.Checkout.open({
      settings: {
        allowLogout: false,
      },
      items: [{ priceId: paddleProductId, quantity: 1 }],
      customer: {
        email,
      },
      customData: {
        fp_tid: getFPTid(),
        "email": email,
      },
      discountCode,
    });
  };

  const inlineCheckout = (planType: Plan, email: string, period: Period) => {
    if (!paddle.value) {
      return;
    }

    const normalizedPeriod: Period = period || 'monthly';

    const paddleProductId = window.__PLANS__.product_prices[normalizedPeriod][planType];
    getTransactionSummaryOrder(paddleProductId);
    paddle.value.Checkout.open({
      settings: {
        allowLogout: false,
        displayMode: "inline",
        frameTarget: 'fallback_checkout_paddle_container',
      },
      items: [{ priceId: paddleProductId, quantity: 1 }],
      customer: {
        email,
      },
      customData: {
        fp_tid: getFPTid(),
        "email": email,
      },
    });
  };

  const updatePaddleUsers = (email: string) => {
    if (!paddle.value) {
      return;
    }

    paddle.value.Update({
      pwCustomer: {
        email,
      },
    });
  };

  const getPaymentMethodLink = async() => {
    const response = await axios.get('/subscription/payment-method-link');

    return response.data?.payment_method_link;
  };

  const checkPaddleRestriction = async() => {
    const response = await axios.get('/check-paddle-restriction');

    return response.data?.is_restricted;
  };

  const getTransactionSummaryOrder = async (priceId: string) => {
    loadingPricePreview.value = true;

    const response = await axios.get(`/subscription/prices/${priceId}`);

    pricePreviewData.value = response.data;

    loadingPricePreview.value = false;

    return response.data;
  };

  return {
    loadPaddle,
    openCheckout,
    updatePaddleUsers,
    paddle,
    loadingPricePreview,
    pricePreviewData,
    getPaymentMethodLink,
    checkPaddleRestriction,
    inlineCheckout,
    getTransactionSummaryOrder,
  };
});
